import {
  LayoutDashboard, Package, ShoppingBag, Tag, Store, Building2, Boxes,
  Truck, Undo2, Landmark, BarChart3, Sparkles, Zap, Users2, FileText,
  GraduationCap, LifeBuoy, Settings, Wallet, Warehouse, TrendingUp,
  Lightbulb, ShoppingCart, Inbox as InboxIcon, Mail, LayoutGrid, ShieldCheck,
  RefreshCw, PlugZap, Scale, UserPlus,
} from "lucide-react";

/*
  Master navigation for Customer Portal V2.
  Each item has:
    - key: unique
    - label: human name
    - icon
    - to: absolute route (reuses existing pages)
    - section: group heading in sidebar
    - roles?: which roles can see (default: everyone except super_admin platform-only tools)
*/

export const V2_NAV = [
  { section: "Workspace", items: [
    { key: "dashboard",     label: "Control Tower",   icon: LayoutDashboard, to: "/dashboard" },
    { key: "my-dashboard",  label: "My Dashboard",    icon: LayoutGrid,       to: "/my-dashboard" },
    { key: "inbox",         label: "My Inbox",        icon: InboxIcon,        to: "/inbox" },
    { key: "orders",        label: "Orders",          icon: Package,          to: "/orders" },
    { key: "products",      label: "Products",        icon: ShoppingBag,      to: "/products" },
    { key: "listings",      label: "Listings",        icon: Tag,              to: "/listings" },
    { key: "marketplace",   label: "Marketplace Hub", icon: Store,            to: "/marketplace-hub" },
  ]},
  { section: "Operations", items: [
    { key: "warehouses",  label: "Warehouses",      icon: Warehouse,        to: "/warehouses" },
    { key: "inventory",   label: "Inventory",       icon: Boxes,            to: "/inventory" },
    { key: "inv-sync",    label: "Sync Center",     icon: RefreshCw,        to: "/inventory-sync" },
    { key: "procurement", label: "Procurement",     icon: ShoppingCart,     to: "/procurement" },
    { key: "shipping",    label: "Shipping",        icon: Truck,            to: "/shipping" },
    { key: "print-center",label: "Print Center",    icon: FileText,         to: "/print-center" },
    { key: "returns",     label: "Returns",         icon: Undo2,            to: "/returns" },
  ]},
  { section: "Finance & Insights", items: [
    { key: "finance",         label: "Finance",         icon: Landmark,         to: "/finance" },
    { key: "finance-reports", label: "Finance Reports", icon: BarChart3,        to: "/finance/reports", role: "super_admin" },
    { key: "company-master",  label: "Company Master",  icon: Building2,        to: "/admin/finance/company-master", role: "super_admin" },
    { key: "my-company",      label: "My Company Master", icon: Building2,      to: "/settings/company-master" },
    { key: "billing-admin",   label: "Billing Dashboard", icon: Wallet,         to: "/admin/billing", role: "super_admin" },
    { key: "billing-portal",  label: "My Billing",      icon: Wallet,           to: "/portal/billing" },
    { key: "legal-admin",     label: "Legal & Compliance", icon: Scale,          to: "/admin/legal", role: "super_admin" },
    { key: "legal-portal",    label: "Legal",           icon: Scale,            to: "/portal/legal" },
    { key: "subscriptions",   label: "Subscriptions",   icon: RefreshCw,        to: "/admin/subscriptions", role: "super_admin" },
    { key: "security",        label: "Security",        icon: ShieldCheck,      to: "/admin/security", role: "super_admin" },
    { key: "invite-users",    label: "Invite Users",    icon: UserPlus,         to: "/admin/users/invite", role: "super_admin" },
    { key: "profile-security",label: "My Security",     icon: ShieldCheck,      to: "/profile/security" },
    { key: "billing",         label: "Billing",         icon: Wallet,           to: "/billing" },
    { key: "reports",         label: "Reports",         icon: BarChart3,        to: "/reports" },
    { key: "bi",              label: "Business BI",     icon: TrendingUp,       to: "/bi" },
  ]},
  { section: "Intelligence", items: [
    { key: "insights",    label: "AI Insights",     icon: Lightbulb,        to: "/insights" },
    { key: "digest",      label: "AI Digest",       icon: Mail,             to: "/digest" },
    { key: "ai",          label: "AI Assistant",    icon: Sparkles,         to: "/ai" },
    { key: "automation",  label: "Workflow Studio", icon: Zap,              to: "/automation" },
    { key: "agency",      label: "Agency Center",   icon: Building2,        to: "/agency" },
    { key: "myntra",      label: "Myntra API V4",   icon: PlugZap,          to: "/integrations/myntra" },
  ]},
  { section: "Platform Admin", role: "super_admin", items: [
    { key: "customers",     label: "Customers",       icon: Users2,           to: "/customers" },
    { key: "admin-console", label: "Admin Console",   icon: ShieldCheck,      to: "/admin/console" },
    { key: "tenants",       label: "Tenants (raw)",   icon: Building2,        to: "/tenants" },
    { key: "plans",         label: "Plans",           icon: Wallet,           to: "/plans" },
    { key: "audit",         label: "Audit Logs",      icon: FileText,         to: "/audit" },
    { key: "doc-templates", label: "Doc Templates",   icon: FileText,         to: "/admin/document-templates" },
    { key: "mp-templates",  label: "MP Templates",    icon: Store,            to: "/admin/marketplace-templates" },
    { key: "doc-health",    label: "Document Health", icon: ShieldCheck,      to: "/admin/document-health" },
  ]},
  { section: "Team", items: [
    { key: "users",       label: "Users",           icon: Users2,           to: "/users" },
    { key: "documents",   label: "Documents",       icon: FileText,         to: "/documents" },
    { key: "training",    label: "Training",        icon: GraduationCap,    to: "/training" },
    { key: "support",     label: "Support",         icon: LifeBuoy,         to: "/support" },
    { key: "settings",    label: "Settings",        icon: Settings,         to: "/settings" },
  ]},
];

// Flat list for command palette
export const V2_NAV_FLAT = V2_NAV.flatMap((g) => g.items.map((i) => ({ ...i, group: g.section })));

/* Additional quick actions for palette */
export const V2_QUICK_COMMANDS = [
  { key: "cmd-add-product",     label: "Add Product",              icon: Package,   to: "/products", group: "Quick Actions" },
  { key: "cmd-create-order",    label: "Create Order",             icon: ShoppingBag, to: "/orders", group: "Quick Actions" },
  { key: "cmd-connect-mp",      label: "Connect Marketplace",      icon: Store,     to: "/marketplace-hub", group: "Quick Actions" },
  { key: "cmd-add-warehouse",   label: "Add Warehouse",            icon: Warehouse, to: "/warehouses", group: "Quick Actions" },
  { key: "cmd-add-user",        label: "Add User",                 icon: Users2,    to: "/users", group: "Quick Actions" },
  { key: "cmd-download-report", label: "Download Reports",         icon: BarChart3, to: "/reports", group: "Quick Actions" },
  { key: "cmd-ai-generate",     label: "AI Generate Listings",     icon: Sparkles,  to: "/ai", group: "Quick Actions" },
];
